import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loader2, Truck, Search } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { StatusTimeline } from "@/components/StatusTimeline";
import { http, API, errMsg, fmtDate, STATUSES } from "@/lib/api";

const LookupForm = () => {
  const nav = useNavigate();
  const [num, setNum] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await http.post("/track/lookup", { order_number: num, email });
      nav(`/suivi/${data.tracking_token}`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="max-w-md space-y-4" data-testid="tracking-lookup-form">
      <Input required placeholder="N° de commande (ex : AC-8F2K1Q)" value={num} onChange={(e) => setNum(e.target.value)} className="h-12 bg-white font-mono uppercase" data-testid="tracking-order-number-input" />
      <Input required type="email" placeholder="E-mail utilisé pour la commande" value={email} onChange={(e) => setEmail(e.target.value)} className="h-12 bg-white" data-testid="tracking-email-input" />
      <button className="btn-gold" disabled={busy} data-testid="tracking-submit-button">{busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Suivre ma commande</button>
    </form>
  );
};

const OrderView = ({ token }) => {
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    const load = () => http.get(`/track/${token}`).then((r) => setO(r.data)).catch((e) => setErr(errMsg(e)));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [token]);
  if (err) return <p className="text-red-700" data-testid="tracking-error">{err}</p>;
  if (!o) return <Loader2 className="animate-spin text-[#7B5338]" />;
  const label = STATUSES.find((s) => s.key === o.status)?.label;
  return (
    <div className="grid lg:grid-cols-12 gap-12" data-testid="tracking-order-view">
      <div className="lg:col-span-4">
        <img src={`${API}/track/${token}/preview`} alt="Aperçu validé" className="w-full rounded-lg shadow-2xl" data-testid="tracking-preview-image" />
      </div>
      <div className="lg:col-span-8 space-y-8">
        <div>
          <p className="font-mono tracking-widest text-[#9A8C7D]" data-testid="tracking-order-number">{o.order_number}</p>
          <h2 className="font-display text-4xl mt-2" data-testid="tracking-current-status">{label}</h2>
          <p className="text-[#6B5F53] mt-2">Commandé le {fmtDate(o.created_at)} · {o.size_label} · {o.price} €</p>
        </div>
        <div className="flex items-center gap-2 text-sm bg-[#EADFD3] rounded-xl p-4 w-fit"><Truck size={16} className="text-[#7B5338]" /> Délai annoncé : <strong>{o.delivery_delay}</strong></div>
        {o.carrier_tracking && <p className="text-sm" data-testid="tracking-carrier-number">N° de suivi transporteur : <span className="font-mono">{o.carrier_tracking}</span></p>}
        <div className="bg-white rounded-2xl border border-[#DDD3C6] p-8"><StatusTimeline status={o.status} history={o.status_history} /></div>
      </div>
    </div>
  );
};

export default function Tracking() {
  const { token } = useParams();
  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 space-y-10">
      <div><span className="label-xs">Suivi de commande</span><h1 className="font-display text-5xl mt-3">Où en est votre tableau ?</h1></div>
      {token ? <OrderView token={token} /> : <LookupForm />}
    </div>
  );
}
